(() => {
  'use strict';
  const tag = 'zionic-atlas-depth';
  const instances = new WeakMap();
  const activeInstances = new Set();
  const entrances = new WeakSet();
  const animations = new Set();
  const elementAnimations = new WeakMap();
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopQuery = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');

  function play(element, frames, duration, delay = 0) {
    if (motionQuery.matches || !element.animate) return;
    elementAnimations.get(element)?.cancel();
    const animation = element.animate(frames, { duration, delay, easing: 'cubic-bezier(.16,1,.3,1)' });
    animation.id = 'za-motion';
    elementAnimations.set(element, animation);
    animations.add(animation);
    const stop = () => animation.cancel();
    element.addEventListener('focusin', stop, { once: true });
    element.addEventListener('pointerdown', stop, { once: true });
    animation.finished.catch(() => {}).finally(() => {
      animations.delete(animation);
      if (elementAnimations.get(element) === animation) elementAnimations.delete(element);
      element.removeEventListener('focusin', stop);
      element.removeEventListener('pointerdown', stop);
    });
  }

  const entranceObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (!isIntersecting) return;
      entranceObserver.unobserve(target);
      target.dataset.zaRevealed = 'true';
      const mobile = !desktopQuery.matches;
      if (target.matches('.za-hero__title')) {
        [...target.children].forEach((line, index) => play(line, [{ transform: `translateY(${mobile ? 12 : 32}px)` }, { transform: 'translateY(0)' }], mobile ? 420 : 920, index * (mobile ? 35 : 70)));
      } else if (target.matches('.za-hero__visual')) {
        const image = target.querySelector('.za-hero__image');
        if (image) play(image, [{ transform: `scale(${mobile ? 1.012 : 1.035})` }, { transform: 'scale(1)' }], mobile ? 480 : 1100);
      } else {
        play(target, [{ transform: `translateY(${mobile ? 8 : 28}px)` }, { transform: 'translateY(0)' }], mobile ? 380 : 760);
      }
    });
  }, { threshold: .08 }) : null;

  motionQuery.addEventListener('change', () => {
    if (motionQuery.matches) animations.forEach((animation) => animation.cancel());
  });

  function initialize(root = document) {
    root.querySelectorAll('.rte table').forEach((table) => {
      table.tabIndex = 0;
    });
    root.querySelectorAll('.za-hero__title, .za-hero__visual, .za-section-heading, .za-chapters__grid, .za-editorial__image-wrap, .za-editorial__copy').forEach((element) => {
      if (entrances.has(element)) return;
      entrances.add(element);
      entranceObserver?.observe(element);
    });
    root.querySelectorAll('[data-za-depth], .za-chapter').forEach((element) => {
      if (instances.has(element)) return;
      const controller = new AbortController();
      let frame = 0;
      const reset = () => {
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
        element.style.removeProperty('--za-depth-x');
        element.style.removeProperty('--za-depth-y');
        element.style.removeProperty('--za-rotate-x');
        element.style.removeProperty('--za-rotate-y');
      };
      element.addEventListener('pointermove', (event) => {
        if (motionQuery.matches || !desktopQuery.matches || event.pointerType !== 'mouse') return;
        if (frame) cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const bounds = element.getBoundingClientRect();
          const x = (event.clientX - bounds.left) / bounds.width - 0.5;
          const y = (event.clientY - bounds.top) / bounds.height - 0.5;
          element.style.setProperty('--za-depth-x', `${(x * 10).toFixed(2)}px`);
          element.style.setProperty('--za-depth-y', `${(y * 10).toFixed(2)}px`);
          const range = element.matches('.za-chapter') ? 6 : 32;
          element.style.setProperty('--za-rotate-x', `${(-y * range).toFixed(2)}deg`);
          element.style.setProperty('--za-rotate-y', `${(x * range).toFixed(2)}deg`);
          frame = 0;
        });
      }, { passive: true, signal: controller.signal });
      element.addEventListener('pointerleave', reset, { signal: controller.signal });
      motionQuery.addEventListener('change', reset, { signal: controller.signal });
      desktopQuery.addEventListener('change', reset, { signal: controller.signal });
      instances.set(element, () => { reset(); controller.abort(); instances.delete(element); activeInstances.delete(element); delete element.dataset.zaInitialized; });
      activeInstances.add(element);
      element.dataset.zaInitialized = tag;
    });
    root.querySelectorAll('media-gallery').forEach((gallery) => {
      if (instances.has(gallery)) return;
      let selected = gallery.querySelector('.product__media-item.is-active');
      const observer = new MutationObserver(() => {
        const current = gallery.querySelector('.product__media-item.is-active');
        if (!current || current === selected) return;
        selected = current;
        const image = current.querySelector('.product__media img');
        if (image) play(image, [{ opacity: .72 }, { opacity: 1 }], desktopQuery.matches ? 320 : 180);
      });
      observer.observe(gallery, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
      instances.set(gallery, () => { observer.disconnect(); instances.delete(gallery); activeInstances.delete(gallery); });
      activeInstances.add(gallery);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    activeInstances.forEach((element) => {
      if (event.target === element || event.target.contains(element)) instances.get(element)?.();
    });
    event.target.querySelectorAll('[data-za-revealed], .za-hero__title, .za-hero__visual, .za-section-heading, .za-chapters__grid, .za-editorial__image-wrap, .za-editorial__copy').forEach((element) => {
      entranceObserver?.unobserve(element);
      entrances.delete(element);
    });
    animations.forEach((animation) => {
      if (animation.effect?.target && event.target.contains(animation.effect.target)) animation.cancel();
    });
  });
})();
